import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import InquiryDialog from "@/components/InquiryDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  skeleton_trailers: "Skeleton Trailers",
  flatbed_trailers: "Flatbed Trailers",
  metals: "Ferrous & Non-Ferrous Metals",
  cargo: "General Cargo",
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/${id}`);
        setProduct(res.data);
        // Fetch related products
        const relRes = await axios.get(`${API}/products`, {
          params: { category: res.data.category, limit: 4 },
        });
        setRelated((relRes.data.products || []).filter((p) => p.id !== id).slice(0, 3));
      } catch (err) {
        console.error("Failed to fetch product:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    setCurrentImage(0);
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="pt-24 pb-20 max-w-7xl mx-auto px-6">
        <div className="animate-pulse">
          <div className="h-6 bg-[#1E1E28] rounded w-24 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-[#1E1E28] rounded-lg" />
            <div className="space-y-4">
              <div className="h-4 bg-[#1E1E28] rounded w-1/3" />
              <div className="h-8 bg-[#1E1E28] rounded w-2/3" />
              <div className="h-20 bg-[#1E1E28] rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-24 pb-20 max-w-7xl mx-auto px-6 text-center">
        <p className="text-[#9CA3AF] text-lg">Product not found</p>
        <Link to="/catalog" className="text-[#DC143C] text-sm mt-2 inline-block">
          Back to catalog
        </Link>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 ? product.images : ["https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800"];

  return (
    <div data-testid="product-detail-page" className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumb */}
        <Link
          to="/catalog"
          data-testid="back-to-catalog"
          className="inline-flex items-center gap-2 text-[#9CA3AF] text-sm hover:text-white transition-colors mb-8"
        >
          <ArrowLeft size={16} /> Back to Catalog
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <div className="relative aspect-square bg-[#0A0A12] border border-white/10 rounded-lg overflow-hidden">
              <img
                data-testid="product-main-image"
                src={images[currentImage]}
                alt={product.title}
                className="w-full h-full object-cover"
              />
              {images.length > 1 && (
                <>
                  <button
                    data-testid="prev-image"
                    onClick={() => setCurrentImage((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    data-testid="next-image"
                    onClick={() => setCurrentImage((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImage(i)}
                    className={`w-16 h-16 rounded border overflow-hidden transition-all ${
                      i === currentImage ? "border-[#DC143C]" : "border-white/10 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-[#000080]/60 text-[#F3F4F6] border border-[#7851A9]/40 uppercase text-[10px] font-bold tracking-widest">
                {product.condition}
              </Badge>
              <span className="text-[#9CA3AF] text-xs uppercase tracking-[0.15em]">
                {CATEGORY_LABELS[product.category] || product.category}
              </span>
            </div>

            <h1
              data-testid="product-title"
              className="text-3xl sm:text-4xl font-black tracking-tighter text-white mb-6"
            >
              {product.title}
            </h1>

            {product.description && (
              <p data-testid="product-description" className="text-[#9CA3AF] text-base leading-relaxed mb-8">
                {product.description}
              </p>
            )}

            <Button
              data-testid="inquire-now-btn"
              onClick={() => setInquiryOpen(true)}
              className="bg-[#DC143C] text-white hover:bg-[#B01030] font-semibold tracking-wide px-8 py-6 text-base rounded-md"
            >
              <Send size={18} className="mr-2" /> Inquire Now
            </Button>

            <div className="mt-10 p-5 bg-[#0A0A12] border border-white/10 rounded-lg">
              <h3 className="text-white font-semibold text-sm uppercase tracking-widest mb-3">Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-[#9CA3AF]">Category</span>
                  <span className="text-white">{CATEGORY_LABELS[product.category] || product.category}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-[#9CA3AF]">Condition</span>
                  <span className="text-white capitalize">{product.condition}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-[#9CA3AF]">Listing ID</span>
                  <span className="text-white font-mono text-xs">{product.id.slice(0, 8)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="text-xl font-bold text-white mb-6">Related Products</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="related-products">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="group bg-[#0A0A12] border border-white/10 rounded-lg overflow-hidden hover:border-[#DC143C]/50 transition-all"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.images?.[0] || "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600"}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <p className="text-white text-sm font-semibold group-hover:text-[#DC143C] transition-colors">{p.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <InquiryDialog
        open={inquiryOpen}
        onOpenChange={setInquiryOpen}
        product={product}
      />
    </div>
  );
}
